import { Component, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import {
  PlacementLevelCode,
  PlacementLevelValues,
} from '../../../pages/domain-summary/models/domain-summary.constant';
import {
  PopoverTableForDomainPerformance,
  StudentPerformance,
} from '../../../pages/domain-summary/models/domain-summary.model';
import { AssessedStudentsPopoverItem, MultiPartProgressBarItem, PlacementLevel } from '../../models/common.model';
import { getPlacementLevelStaticLabel } from '../../sharedUtils';
import { BaseComponent } from '../base.component';

@Component({
  selector: 'app-multi-progress-bar',
  templateUrl: './multi-progress-bar.component.html',
  styleUrls: ['./multi-progress-bar.component.scss'],
})
export class MultiProgressBarComponent extends BaseComponent implements OnDestroy {
  @Input() multiPartProgressBarItems: MultiPartProgressBarItem[] = [];
  @Input() report: Map<PlacementLevelCode, StudentPerformance[]>;
  @Input() placementLevels: PlacementLevel[];

  staticColorText: string;
  placementLevelCodeId: string;
  popoverHeaders = [];
  assessedStudentsPopoverItems: AssessedStudentsPopoverItem[] = [];
  multiPartProgressBarItemId = '';
  multiPartProgressBarEvent: CustomEvent;
  selectedPlacementLevel = '';
  popoverTableData: PopoverTableForDomainPerformance = {};

  constructor(private readonly translateService: TranslateService) {
    super();
  }

  onMultiPartProgressBarItemOnHover(event: CustomEvent): void {
    this.popoverHeaders = [];
    this.assessedStudentsPopoverItems = [];
    this.multiPartProgressBarEvent = event;
    this.setHeaderForPopOver(event);
  }

  setHeaderForPopOver(multiPartProgressBarEvent: CustomEvent): void {
    switch (multiPartProgressBarEvent.detail.id) {
      case PlacementLevelCode.DNM:
        this.setDataForPopOverHeader(PlacementLevelValues.DNM, multiPartProgressBarEvent);
        break;
      case PlacementLevelCode.APP:
        this.setDataForPopOverHeader(PlacementLevelValues.APP, multiPartProgressBarEvent);
        break;
      case PlacementLevelCode.MET:
        this.setDataForPopOverHeader(PlacementLevelValues.MET, multiPartProgressBarEvent);
        break;
      case PlacementLevelCode.EXC:
        this.setDataForPopOverHeader(PlacementLevelValues.EXC, multiPartProgressBarEvent);
        break;
      default:
        break;
    }
    this.translateService
      .get('domain-summary.assessed-students-popup.score')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((translateText) => this.popoverHeaders.push(translateText));
    this.setPopoverData(multiPartProgressBarEvent.detail.id);
  }

  setPopoverData(multiPartProgressBarItemId: PlacementLevelCode): void {
    const individualGrowthReports = this.report.get(multiPartProgressBarItemId);
    individualGrowthReports
      .sort((student1, student2) => student1.studentName.localeCompare(student2.studentName))
      .forEach((reportData) => {
        const { studentId, studentName, performance } = reportData;
        this.assessedStudentsPopoverItems.push({
          studentId,
          studentName,
          score: performance.scaleScore,
        });
      });
    this.selectedPlacementLevel = getPlacementLevelStaticLabel(this.placementLevels, multiPartProgressBarItemId);
    this.popoverTableData = {
      isStaticUiTextForHeader: true,
      isViewReportLink: true,
      staticColorText: this.staticColorText,
      staticUiTextForPlacementLevel: this.selectedPlacementLevel,
      placementLevelCodeId: this.placementLevelCodeId,
    };
  }

  private setDataForPopOverHeader(
    placementLevel: PlacementLevelValues,
    multiPartProgressBarEvent: CustomEvent,
  ): void {
    this.multiPartProgressBarItemId = this.translateService.instant(placementLevel);
    this.popoverHeaders.push(this.multiPartProgressBarItemId);
    this.staticColorText = multiPartProgressBarEvent.detail.fillColor;
    this.placementLevelCodeId = multiPartProgressBarEvent.detail.id;
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
